import Page from "@/containers/Page";
import { useDonations } from "@/hooks/useDonations";
import { Donations } from "@/types/models/Donations";
import { currency } from "@/lib/format";
import { usePageInfo } from "@/hooks/usePageInfo";

import {
  BsChat,
  BsCoin,
  BsDownload,
  BsPersonFillAdd,
  BsPersonHeart,
  BsThreeDots,
} from "react-icons/bs";
import { downloadDonations } from "@/services/donations.service";
import { generateFilenameWithDateTime } from "@/lib/utils";
import MyButton from "@/components/Button";
import { useRouter } from "next/router";
import Loader from "../components/Loader";
import { GoChevronLeft, GoChevronRight } from "react-icons/go";
import { TiArrowLoop } from "react-icons/ti";
import { PaginationButton } from "../components/Table";
import Tooltip from "../components/ToolTip";
import { HeaderToolTip } from "@/components/HeaderToolTip";
import { usePreloadDonor } from "@/hooks/useDonor";
import { useState } from "react";
import NewOfflineDonation from "@/containers/NewOfflineDonation";
import ModalOfflineDonation from "@/containers/ModalOfflineDonation";

export default function DonationsPage() {
  const [showOfflineModal, setShowOfflineModal] = useState<boolean>(false);
  const {
    donations,
    page,
    pages,
    next,
    previous,
    canNext,
    canPrevious,
    isLoading,
    total,
    preloadData,
    refresh,
  } = useDonations({
    initialPage: 0,
    limit: 10,
    sortBy: "createdAt",
    sortDirection: "DESC",
  });

  const [selectedDonation, setSelectedDonation] = useState<
    Donations | undefined
  >();

  const { preloadDonor } = usePreloadDonor();
  const router = useRouter();

  const handleDonorClick = (donation: Donations) => {
    router.push(`/donors/${donation.donor.id}`);
  };

  const routeInfo = usePageInfo();

  const handleDownload = async () => {
    const content = await downloadDonations();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([content], { type: "text/csv" }));
    a.download = generateFilenameWithDateTime("donations", "csv");
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    return {} as any;
  };
  const onNewDonation = () => {
    refresh();
    setShowOfflineModal(false);
  };

  return (
    <Page
      title={`${routeInfo.name} | GoGive`}
      header={`${routeInfo.name!} ${!isLoading ? `(${total})` : ""}`}
      Icon={routeInfo.icon!}
      headerChildren={
        <div className="flex items-center space-x-4">
          <MyButton
            disabled={total === 0}
            variant="outline"
            label="Export"
            containerStyle={{
              height: 32,
              borderRadius: 8,
              px: 32,
            }}
            labelStyle={{ fontSize: "14px" }}
            Icon={BsDownload}
            onClick={handleDownload}
          />
          <NewOfflineDonation onSave={onNewDonation} />
        </div>
      }
    >
      <div className="bg-white rounded-sm p-3">
        <table className="min-w-full text-left text-sm font-light">
          <thead className="border-b font-medium border-pink-200">
            <tr className="pb-4 ">
              <th className="px-6 py-4 font-normal text-gray-500">Date </th>
              <th className="px-6 py-4 font-normal text-gray-500">Name</th>
              <th className="px-6 py-4 font-normal text-gray-500">Type</th>
              <th className="px-6 py-4 font-normal text-gray-500 flex items-center ">
                <HeaderToolTip header="Gross" tip="How much your donor paid" />
              </th>
              <th className="px-6 py-4 font-normal text-gray-500">
                <HeaderToolTip header="Stripe" tip="The Stripe fee and tax" />
              </th>

              <th className="px-6 py-4 font-normal text-gray-500">
                <HeaderToolTip header="Net" tip="How much you receive" />
              </th>
              <th className="px-6 py-4 font-normal text-gray-500">Campaign</th>
              <th className=""></th>
            </tr>
          </thead>
          <tbody>
            {donations.map((donation) => (
              <tr
                key={donation.id}
                className="border-b border-pink-100 last:border-b-0 hover:bg-pink-50  transition-all ease-in-out duration-200 "
              >
                <td className="whitespace-nowrap px-1 md:px-6 py-5 font-normal text-sm md:text-md text-gray-600 ">
                  {new Date(donation.createdAt).toLocaleString()}
                </td>
                <td
                  className="whitespace-nowrap px-1 md:px-6 py-5 font-semibold text-sm md:text-md text-gray-600 cursor-pointer "
                  onClick={() => handleDonorClick(donation)}
                  onMouseEnter={() =>
                    donation.donor ? preloadDonor(donation.donor.id) : undefined
                  }
                >
                  {donation.donor?.company && (
                    <div className="text-gray-500 font-normal">
                      {donation.donor.company}
                    </div>
                  )}
                  <div className="underline">
                    {donation.donor
                      ? `${donation.donor.firstName} ${donation.donor.lastName}`
                      : "Anonymous"}
                  </div>
                </td>
                <td className="whitespace-nowrap px-3 md:px-6 py-5 font-normal text-sm md:text-md  space-x-2">
                  <div className="inline-flex space-x-2 items-center ">
                    <TypeTag tag={donation.type} />
                    {donation.offline && (
                      <Tooltip
                        content={
                          <div className="w-full text-xs">
                            {"offline donation"}
                          </div>
                        }
                        position="right"
                      >
                        <div className="text-primary">
                          <BsPersonFillAdd size={16} />
                        </div>
                      </Tooltip>
                    )}
                  </div>
                </td>

                <td className="whitespace-nowrap px-6 py-5 font-normal">
                  {currency(
                    (donation.grossAmount - (donation.gogiveAmount ?? 0)) / 100,
                    2
                  )}
                </td>
                <td className="whitespace-nowrap px-6 py-5 font-normal">
                  {currency((donation.stripeFee + donation.stripeTax) / 100, 2)}
                </td>

                <td className="whitespace-nowrap px-6 py-5 font-semibold">
                  {currency(donation.netAmount / 100, 2)}
                </td>
                <td className="whitespace-nowrap px-6 py-5 font-normal">
                  {donation.campaign?.name ?? ""}
                </td>
                <td className="px-6 py-5 font-normal">
                  <Tooltip
                    content={
                      <div className="min-w-[188px]">{donation.comment}</div>
                    }
                    position="left"
                  >
                    {!!donation.comment && <BsChat size={20} />}
                  </Tooltip>
                </td>
                {donation.offline && (
                  <td
                    className="cursor-pointer"
                    onClick={() => setSelectedDonation(donation)}
                  >
                    <div className="text-red-600">
                      <BsThreeDots size={14} />
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>

        {total > 0 || page > 1 ? (
          <>
            {previous && next && (pages ?? 0) > 1 && (
              <div className="flex justify-end items-center space-x-4">
                <div className="text-sm">{`Page ${page} of ${pages}`}</div>
                <div className="flex ">
                  <PaginationButton
                    onClick={previous}
                    Icon={GoChevronLeft}
                    disabled={!canPrevious}
                    onHover={() => preloadData(-1)}
                  />
                  <PaginationButton
                    onClick={next}
                    Icon={GoChevronRight}
                    disabled={!canNext}
                    onHover={() => preloadData(1)}
                  />
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="py-11 text-center text-md text-gray-500">
            {isLoading ? (
              <Loader size={24} />
            ) : (
              <> There are no donations to display yet</>
            )}
          </div>
        )}
      </div>
      {selectedDonation && (
        <ModalOfflineDonation
          donation={selectedDonation}
          isOpen={!!selectedDonation}
          onClose={() => setSelectedDonation(undefined)}
          onSave={refresh}
        />
      )}
    </Page>
  );
}

export const TypeTag = ({ tag }: { tag: string }) => {
  const onetimeClass =
    "text-center font-semibold text-xs text-green-900 rounded-full p-1 bg-green-100 border-green-400 border-[1px] ";
  const roundupClass =
    "text-center font-semibold text-xs text-orange-900 rounded-full p-1 bg-orange-100 border-orange-400 border-[1px] ";
  const recurringClass =
    "text-center font-semibold text-xs text-purple-900 rounded-full p-1 bg-purple-100 border-purple-400 border-[1px] ";
  const style =
    tag === "onetime"
      ? onetimeClass
      : tag === "recurring"
      ? recurringClass
      : roundupClass;
  return (
    <div className="flex">
      <div className={style}>
        <div className="">
          <Tooltip
            content={<div className="w-full text-xs font-normal">{tag}</div>}
            position="right"
          >
            <div className="">
              {tag === "onetime" && <BsPersonHeart size={12} />}
              {tag === "roundup" && <BsCoin size={12} />}
              {tag === "recurring" && <TiArrowLoop size={12} />}
            </div>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};
